import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Collapse, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { get, times } from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  activityTypesTextMap,
  customerActivityChannelTextMap,
} from '../../../context/SystemSettingsContext';
import { Activity } from '../../../models/activity.model';
import { Order, getComparator } from '../../../utils/tableHelpers';
import doesRoleExist from '../../../utils/doesRoleExist';
import { useAuth } from '../../../context/AuthContext';

export interface Data {
  id: string;
  channel: string;
  type: string;
  date: string;
  note: string;
  createdBy: {
    email: string;
    firstName: string;
    id: number;
    lastName: string;
  };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'id',
    numeric: false,
    disablePadding: true,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_ID',
  },
  {
    id: 'channel',
    numeric: false,
    disablePadding: true,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_CHANNEL',
  },
  {
    id: 'type',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_TYPE',
  },
  {
    id: 'date',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_DATE',
  },
  {
    id: 'createdBy',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_CREATED_BY',
  },
  {
    id: 'note',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_NOTE',
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const { t } = useTranslation();

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.label)}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  activityId: string;
  deleteTeam: (teamId: string) => void;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected, activityId, deleteTeam } = props;
  const { t } = useTranslation();
  const { user, organization } = useAuth();

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 && !doesRoleExist(user, organization, ['OBSERVER']) ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {activityId} {t('CUSTOMERS.CUSTOMER_DETAILS.SELECTED')}
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {t('CUSTOMERS.CUSTOMER_DETAILS.TAB_ACTIVITIES')}
        </Typography>
      )}
      {numSelected > 0 && (
        <Tooltip title={t('ORGANIZATION.TEAM.DELETE')}>
          <IconButton
            onClick={() => deleteTeam(activityId)}
            data-testid="deleteActivity"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

export default function CustomerActivitiesTable({
  rows,
  deleteActivity,
  setSelectedActivity,
  isLoading,
}: {
  rows: Data[];
  deleteActivity: (documentId: string) => void;
  setSelectedActivity: Dispatch<SetStateAction<Activity | undefined>>;
  isLoading: boolean;
}) {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [selected, setSelected] = React.useState<string>('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { user, organization } = useAuth();

  useEffect(() => {
    if (!rows.find((row) => row.id === selected)) {
      setSelected('');
    }
  }, [rows, selected]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: string) => {
    if (
      !doesRoleExist(user, organization, [
        'ADMIN',
        'TEAM_MANAGER',
        'TEAM_MEMBER',
      ])
    ) {
      return;
    }

    setSelected((prevSelected) => {
      return prevSelected !== id ? id : '';
    });
    const row = rows.find((row) => row.id === id);
    if (row) {
      setSelectedActivity(row as Activity);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const Row = ({
    row,
    isItemSelected,
    labelId,
  }: {
    row: Data;
    isItemSelected: boolean;
    labelId: string;
  }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    return (
      <>
        <TableRow
          hover
          onClick={(event) => handleClick(event, row.id)}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          key={row.id}
          data-testid={row.id}
          selected={isItemSelected}
        >
          <TableCell padding="checkbox">
            {doesRoleExist(user, organization, [
              'ADMIN',
              'TEAM_MEMBER',
              'TEAM_MANAGER',
            ]) && (
              <Checkbox
                color="primary"
                checked={isItemSelected}
                inputProps={{
                  'aria-labelledby': labelId,
                }}
              />
            )}
          </TableCell>
          <TableCell component="th" id={labelId} scope="row" padding="none">
            {row.id}
          </TableCell>
          <TableCell align="left">
            {t(get(customerActivityChannelTextMap, row.channel))}
          </TableCell>
          <TableCell align="left">
            {t(get(activityTypesTextMap, row.type))}
          </TableCell>
          <TableCell align="left">{row.date}</TableCell>
          <TableCell align="left">{row.createdBy.email}</TableCell>
          <TableCell align="left">
            <IconButton
              aria-label="expand row"
              size="small"
              data-testid="userDetails"
              onClick={async (e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="body1" gutterBottom component="div">
                  {row.note}
                </Typography>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const isSelected = ($id: string) => selected === $id;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  let rowDisplay;
  if (isLoading) {
    rowDisplay = times(3, (index) => {
      return (
        <TableRow key={index}>
          <TableCell padding="checkbox">
            <Skeleton animation="wave" />
          </TableCell>
          {times(5, (index) => (
            <TableCell align="left" key={index}>
              <Skeleton animation="wave" />
            </TableCell>
          ))}
        </TableRow>
      );
    });
  } else {
    rowDisplay = rows
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => {
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;
        return (
          <Row
            data-testid={row.id}
            key={row.id}
            row={row}
            isItemSelected={isItemSelected}
            labelId={labelId}
          />
        );
      });
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="customerActivitiesTable">
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          activityId={selected}
          deleteTeam={deleteActivity}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rowDisplay}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-testid="rowsPerPage"
        />
      </Paper>
    </Box>
  );
}
